<template>
  <LeadHistoryCard
    :text="nextActionText"
    :user="nextAction.doneBy || {id: null}"
    icon="check"
    icon-type="success"
    :call-target-id="callTargetId"
    :call-target="callTarget"
    :history="nextAction"
    :expand="expand"
    :pick-up-call-target-id="pickUpCallTargetId"
    @update:pick-up-call-target-id="$emit('update:pickUpCallTargetId', $event)"
  >
    <template #date>
      <div>{{ getTimeWithDateAndTimeFilter(nextAction.doneAt) }}</div>
    </template>
    <template
      v-if="innerEditable"
      #action
    >
      <BTooltip top>
        <BBtn
          v-if="deletable"
          size="small"
          fab
          flat
          @click.stop="handleDoneCancel"
        >
          <BIcon size="small">
            clear
          </BIcon>
        </BBtn>
        <template #content>
          <div class="save-text">
            {{ $t('general.revoke') }}
          </div>
        </template>
      </BTooltip>
    </template>
    <div class="history-card-body">
      <div class="call-result-detail-link-container">
        <CallResultDetailLink
          v-if="nextAction.nextActionSuggestionCallResultId"
          :call-result-id="nextAction.nextActionSuggestionCallResultId"
        />
      </div>
      <BPriorityPreview
        v-if="nextAction.priority !== 'not_set'"
        :priority="nextAction.priority"
      />
      <BEditableContentTextarea
        v-model="content"
        class="card-item next-action-comment-area"
        :placeholder="$t('nextAction.content')"
        :editable="innerEditable"
        @update:model-value="updateNextActionContent"
        @click="setTargetHeaderMoveButtonDisable(true)"
        @cancel="setTargetHeaderMoveButtonDisable(false)"
      />
      <BLayout
        v-if="content"
        justify-end
        class="mt-50"
      >
        <BCopyButton
          :text="content"
          :title="$t('nextAction.content')"
          is-detailed-name
        />
      </BLayout>
    </div>
  </LeadHistoryCard>
</template>

<script>
import { mapWaitingActions } from 'vue-wait';
import CallResultDetailLink from '@/components/organisms/user/general/CallResultDetailLink.vue';
import LeadHistoryCard from '@/components/organisms/user/lead/history/LeadHistoryCard.vue';
import { useErrorHandler } from '@/composable/error-handler';
import { altText } from '@/utils/alt';
import { formatShorterDateTime } from '@/utils/date-time';

export default {
  components: {
    LeadHistoryCard,
    CallResultDetailLink,
  },
  inheritAttrs: false,
  props: {
    callTargetId: {
      type: Number,
      required: true,
    },
    nextAction: {
      type: Object,
      required: true,
    },
    callTarget: {
      type: Object,
      required: false,
      default: undefined,
    },
    expand: {
      type: Boolean,
      default: true,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    pickUpCallTargetId: {
      type: Number,
      required: false,
      default: undefined,
    },
  },
  emits: [
    'done-cancel',
    'update:pickUpCallTargetId',
  ],
  setup() {
    const { defaultHandler } = useErrorHandler();
    return {
      defaultHandler,
    };
  },
  data () {
    return {
      content: this.nextAction.content,
    };
  },
  computed: {
    nextActionText () {
      return this.nextAction.name + this.$t(`general.done.text`);
    },
    deletable() {
      return this.nextAction.deletable;
    },
    innerEditable() {
      return this.editable && this.nextAction.editable;
    },
  },
  watch: {
    'nextAction.content' (newVal) {
      this.content = newVal;
    },
  },
  methods: {
    ...mapWaitingActions('user', {
      updateNextActionContentAction: 'updateNextActionContentAction',
      getCallTargetsBackgroundAction: { action: 'getCallTargetsAction', loader: 'getCallTargetsBackgroundWait' },
      getLeadHistoriesBackgroundAction: { action: 'getLeadHistoriesAction', loader: 'getLeadHistoriesBackgroundWait' },
    }),
    ...mapWaitingActions('userUi', {
      setTargetHeaderMoveButtonDisabledAction: 'setTargetHeaderMoveButtonDisabledWait',
    }),
    setTargetHeaderMoveButtonDisable (status) {
      this.setTargetHeaderMoveButtonDisabledAction(status);
    },
    updateNextActionContent () {
      this.setTargetHeaderMoveButtonDisabledAction(false);
      this.updateNextActionContentAction({
        nextActionId: this.nextAction.id,
        callTargetId: this.nextAction.callTargetId,
        body: { content: this.content },
        errorHandlers: {
          422: this.defaultHandler,
        },
      }).then(() => {
        return this.getLeadHistoriesBackgroundAction({ callTargetId: this.nextAction.callTargetId });
      }).then(() => {
        return this.getCallTargetsBackgroundAction({ callListId: this.$route.params.id });
      });
    },
    handleDoneCancel () {
      const nextAction = this.$JSON.parse(this.$JSON.stringify(this.nextAction));
      this.$emit('done-cancel', nextAction);
    },
    getTimeWithDateAndTimeFilter (time) {
      return altText(formatShorterDateTime(time));
    },
  },
};
</script>

<style lang="scss" scoped>
  .nextaction-card {
    padding: $basespace-500 16px $basespace-500 80px;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  .created-at {
    display: inline-block;
    width: 120px;
  }

  .history-card-body {
    white-space: pre-wrap;
    word-wrap: break-word;

    .priority-preview {
      margin-left: calc(#{$basespace-100} + 2px);
      margin-bottom: $basespace-50;
    }
  }
</style>
